import { Form, Row, Col } from "react-bootstrap";
import { useForm, FormProvider } from "react-hook-form";
import { Input, MultiSelect, PageWrapper, PasswordInput, Select } from "../../components";
import { useTranslation } from "react-i18next";
import { createUser, getUserDetail, updateUser } from "../../api/user";
import { getAdminPortalRoleList } from "../../api/role";
import { getServiceAreaOptions } from "../../api/service-area";
import { Pages } from '../../shared/enums'
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { RiAddLargeLine, RiEdit2Fill } from "react-icons/ri";
import Button from "../../components/common/Button"

const AdminDetail = () => {
    const { t } = useTranslation()
    const form = useForm()
    const navigate = useNavigate()
    const { id } = useParams()
    const [isEdit, setIsEdit] = useState(false)
    const [roles, setRoles] = useState([])
    const [serviceAreaOptions, setServiceAreaOptions] = useState([])
    const [btnLoading, setBtnLoading] = useState(false)

    useEffect(() => {
        getRoles()
        getServiceAreas()
        if (id !== "new") {
            setIsEdit(true)
            getData()
        } else {
            form.reset({ countryCode: "+60", serviceAreas: [] })
        }
    }, [])

    const getData = async () => {
        const response = await getUserDetail(id)
        form.reset({
            ...response,
            roles: response.roles[0].id,
            serviceAreas: toServiceAreaOptions(response.serviceAreas),
            suspended: response.suspended.toString()
        })
    }

    const getRoles = async () => {
        const response = await getAdminPortalRoleList(id)
        setRoles(response.map(e => ({ label: e.name.toUpperCase().replaceAll(" ", ""), value: e.id })))
    }

    const getServiceAreas = async () => {
        const response = await getServiceAreaOptions()
        setServiceAreaOptions(response)
    }

    const create = async (data) => {
        setBtnLoading(true)
        const response = await createUser(toPayload(data))
        if (response.success) navigate(Pages.Admin)
        setBtnLoading(false)
    }

    const update = async (data) => {
        setBtnLoading(true)
        const response = await updateUser(id, toPayload(data))
        if (response.success) navigate(Pages.Admin)
        setBtnLoading(false)
    }

    const toServiceAreaOptions = (serviceAreas = []) => {
        return serviceAreas.map((serviceArea) => {
            return serviceAreaOptions.find((option) => option.value === serviceArea) ?? {
                label: serviceArea,
                value: serviceArea,
            }
        })
    }

    const toPayload = (data) => ({
        ...data,
        roles: [data.roles],
        serviceAreas: (data.serviceAreas ?? []).map((serviceArea) => serviceArea.value ?? serviceArea),
    })

    return <PageWrapper
        headerProps={{
            title: isEdit ? "EDIT_ADMIN" : "ADD_ADMIN",
            rightElement: <Button type="submit" onClick={() => form.handleSubmit(isEdit ? update : create)()} variant="primary" icon={isEdit ? <RiEdit2Fill /> : <RiAddLargeLine />} loading={btnLoading}> {t(isEdit ? "UPDATE" : "CREATE")}</Button>

        }}
    >
        <FormProvider {...form}>
            <Form>
                <Row>
                    <Col sm={12} md={6}>
                        <Input label={t("FIRST_NAME")} name={"firstName"} rules={{ required: t("FIRST_NAME_IS_REQUIRED") }} />
                    </Col>
                    <Col sm={12} md={6}>
                        <Input label={t("LAST_NAME")} name={"lastName"} rules={{ required: t("LAST_NAME_IS_REQUIRED") }} />
                    </Col>
                </Row>
                <Row>
                    <Col sm={12} md={6}>
                        <Input
                            label={t("EMAIL")}
                            name={"email"}
                            type="email"
                            placeholder={"e.g. email@example.com"}
                            rules={{
                                required: t("EMAIL_IS_REQUIRED"), pattern: {
                                    value: /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/,
                                    message: t("INVALID_EMAIL")
                                }
                            }} />
                    </Col>
                    <Col sm={12} md={6}>
                        {!isEdit && <PasswordInput
                            label={t("PASSWORD")}
                            name={"password"}
                            rules={{
                                required: t("PASSWORD_IS_REQUIRED"),
                                pattern: {
                                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]{8,}$/,
                                    message: t("PASSWORD_MUST_CONTAIN_UPPERCASE_LOWERCASE_NUMBER_SYMBOL")
                                }
                            }} />}
                    </Col>
                </Row>
                <Row>
                    <Col style={{ flex: 0 }} className="pe-0">
                        <Select
                            label={t("COUNTRY_CODE")}
                            name={"countryCode"}
                            rules={{ required: t("COUNTRY_CODE_IS_REQUIRED") }}
                            options={[
                                { label: "+60", value: "+60" },
                            ]}
                            containerProps={{ style: { width: "120px" } }}
                        />
                    </Col>
                    <Col style={{ flex: 1 }} >
                        <Input
                            label={t("PHONE_NUMBER")}
                            name={"phoneNumber"}
                            placeholder={"e.g. 123456789"}
                            rules={{
                                required: t("PHONE_NUMBER_IS_REQUIRED"),
                                pattern: {
                                    value: /^[0-9]{8,12}$/, // A basic 10-digit number pattern (adjust as needed)
                                    message: t("INVALID_PHONE_NUMBER")
                                }
                            }}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col xs={6} md={4}>
                        <Select
                            label={t("GENDER")}
                            name={"gender"}
                            rules={{ required: t("GENDER_IS_REQUIRED") }}
                            options={[
                                { label: t("MALE"), value: "male" },
                                { label: t("FEMALE"), value: "female" },
                            ]}
                        />
                    </Col>

                    {isEdit && <Col xs={6} md={4}>
                        <Select
                            label={t("SUSPENDED")}
                            name={"suspended"}
                            rules={{ required: t("SUSPENDED_IS_REQUIRED") }}
                            options={[
                                { label: t("YES"), value: "true" },
                                { label: t("NO"), value: "false" },
                            ]}
                        />
                    </Col>
                    }
                    <Col xs={6} md={4}>
                        <Select
                            label={t("ROLE")}
                            name={"roles"}
                            rules={{ required: t("ROLE_IS_REQUIRED") }}
                            options={roles}
                        />
                    </Col>
                    <Col xs={12} md={8}>
                        <MultiSelect
                            label={t("SERVICE_AREAS")}
                            name={"serviceAreas"}
                            options={serviceAreaOptions}
                        />
                    </Col>
                </Row>
            </Form>
        </FormProvider>
    </PageWrapper>
}

export default AdminDetail;
